import axios from "axios";
import { API_BASE_URL } from "../utils/apiConfig";

// Build the full API URL - if API_BASE_URL doesn't include /api/v1, add it
const getFullApiUrl = () => {
  if (API_BASE_URL.includes("/api/v1")) {
    return API_BASE_URL;
  }
  // If it's just the base URL (e.g., http://localhost:8000), add /api/v1
  return `${API_BASE_URL}/api/v1`;
};

const authApi = axios.create({
  baseURL: `${getFullApiUrl()}/auth`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors with refresh token
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          // Import axios dynamically to avoid interceptor conflicts
          const { default: freshAxios } = await import("axios");

          const refreshResponse = await freshAxios.post(
            `${getFullApiUrl()}/auth/refresh?frontend=chat`,
            {
              refresh_token: refreshToken,
            },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const refreshData = refreshResponse.data;

          // Update tokens in localStorage
          localStorage.setItem("access_token", refreshData.access_token);
          if (refreshData.id_token) {
            localStorage.setItem("id_token", refreshData.id_token);
          }
          if (refreshData.refresh_token) {
            localStorage.setItem("refresh_token", refreshData.refresh_token);
          }

          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${refreshData.access_token}`;

          // Retry the original request
          return authApi(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          console.error("❌ AuthService: Token refresh failed:", refreshError);
          localStorage.removeItem("access_token");
          localStorage.removeItem("id_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/";
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token available, clear tokens and redirect to login
        console.log("AuthService: No refresh token available");
        localStorage.removeItem("access_token");
        localStorage.removeItem("id_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/";
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Initiate login flow
  async initiateLogin() {
    try {
      const response = await authApi.post("/login", null, {
        params: { frontend: "chat" }
      });
      return response.data;
    } catch (error) {
      // Provide specific error messages based on the error type
      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        throw new Error(
          "Não foi possível ligar ao servidor. Por favor tente novamente mais tarde."
        );
      } else if (error.response?.status === 500) {
        throw new Error(
          "O servidor está temporariamente indisponível. Por favor tente novamente em breve."
        );
      } else if (error.response?.status === 503) {
        throw new Error(
          "O serviço está temporariamente indisponível. Por favor tente novamente mais tarde."
        );
      } else {
        throw new Error("Falha no início de sessão. Por favor tente novamente.");
      }
    }
  },

  // Handle OIDC callback with retry logic
  async handleCallback(code, state, maxRetries = 3) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await authApi.get("/callback", {
          params: { code, state, frontend: "chat" },
        });
        return response.data;
      } catch (error) {
        lastError = error;

        // If it's a 500 error and we have more attempts, wait and retry
        if (error.response?.status === 500 && attempt < maxRetries) {
          const delay = attempt * 1000; // 1s, 2s, 3s delays
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // If it's not a 500 error or we've exhausted retries, throw immediately
        break;
      }
    }

    // Callback failed after all retries
    // Provide more specific error messages based on the last error
    if (lastError?.response?.status === 500) {
      throw new Error(
        "Erro no servidor: O servidor de autenticação está com problemas. Por favor tente novamente em breve."
      );
    } else if (lastError?.response?.status === 403) {
      throw new Error(
        "Acesso negado: Não tem as permissões necessárias. Por favor contacte o seu administrador."
      );
    } else if (lastError?.response?.status === 400) {
      throw new Error(
        "Erro de autenticação: Pedido inválido. Por favor tente iniciar sessão novamente a partir da página principal."
      );
    } else if (lastError?.code === "ERR_NETWORK") {
      throw new Error(
        "Erro de rede: Não foi possível ligar ao servidor de autenticação. Por favor verifique a sua ligação à internet."
      );
    } else {
      throw new Error(
        "Falha no início de sessão: Não foi possível completar a autenticação. Por favor tente novamente."
      );
    }
  },

  // Refresh access token using refresh token
  async refreshToken(refreshToken) {
    try {
      const response = await authApi.post("/refresh", {
        refresh_token: refreshToken,
      }, {
        params: { frontend: "chat" }
      });
      return response.data;
    } catch (error) {
      throw new Error("Falha ao renovar a sessão");
    }
  },

  // Get current user info
  async getCurrentUser() {
    try {
      const response = await authApi.get("/user");
      return response.data;
    } catch (error) {
      throw new Error("Falha ao obter informações do utilizador");
    }
  },

  // Logout
  async logout() {
    try {
      const response = await authApi.post("/logout", null, {
        params: { frontend: "chat" }
      });
      return response.data;
    } catch (error) {
      throw new Error("Falha ao terminar sessão");
    }
  },
};
